"use client";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import { detectAndSaveGeoPosition } from "@/utils/geolocation";

import EventCard from "../../Elements/Card";
import CustomSearchBar from "../../Elements/CustomSearchBar";
import { useAppState } from "../../../appContext";
import ChecklistFilter from "../Filters/ChecklistFilter";

import EventAccordion from "./EventAccordion";
import { useRouter } from "next/router";
import { EventDetails } from "./EventDetails";
import { useTheme } from "@mui/material/styles";
import AppliedFilters from "../Filters/AppliedFilters";
import LocationFilter from "../Filters/LocationFilter";
import DeleteIcon from "@mui/icons-material/Delete";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { useNotification } from "../../Notification";
import { useFetchEvents } from "../../../hooks";
import isEqual from "lodash/isEqual";
import YearFilter from "../Filters/YearFilter";
import DateFilter from "../Filters/DateFilter";
import { useDebounce } from "../../../utils";
import VenueFilter from "../Filters/VenueFilter";
import firebase from "firebase/compat/app";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "@/utils/firebase-config";
import BandFilter from "../Filters/BandFilter";
import useIsSmallScreen from "./../../../utils/useIsSmallScreen";
import UseMyLocationButton from "../../Elements/UseMyLocationButton";
import { useFilterContext } from "@/appContext/FilterContext";
import dynamic from "next/dynamic";
const RecommendedRow = dynamic(() => import("@/components/RecommendedRow"), {
  ssr: false,
});

const NYC_REGION = {
  label: "New York City",
  state: "NY",
  // MUST match your Firestore events.city values exactly
  cities: ["New York", "Brooklyn", "Queens", "Bronx", "Staten Island"],
};

const ANNAPOLIS_REGION = {
  label: "Annapolis",
  state: "MD",
  // Must exactly match your Firestore events.city values
  cities: [
    "Annapolis",
    "Arnold",
    "Severna Park",
    "Edgewater",
    "Riva",
    "Crownsville",
    "Davidsonville",
    "Crofton",
    "Gambrills",
    "Pasadena",
  ],
};

const BALTIMORE_REGION = {
  label: "Baltimore",
  state: "MD",
  cities: [
    "Baltimore",
    "Towson",
    "Catonsville",
    "Glen Burnie",
    "Ellicott City",
    "Columbia",
    "Parkville",
    "Pikesville",
    "Owings Mills",
    "Cockeysville",
    "Hunt Valley",
    "Dundalk",
    "Halethorpe",
    "Baltimore Highlands",
  ],
};

const LOCATION_REGIONS = {
  "long-island": {
    label: "Long Island",
    state: "NY",
    cities: [
      "Hempstead",
      "Garden City",
      "Rockville Centre",
      "Long Beach",
      "Great Neck",
      "Port Washington",
      "Westbury",
      "Huntington",
      "Huntington Station",
      "Patchogue",
      "Bay Shore",
      "Amityville",
      "Sayville",
      "Riverhead",
      "Sag Harbor",
      "Southampton",
      "Montauk",
      "Greenport",
      "Smithtown",
    ],
  },
  nyc: NYC_REGION,
  "new-york-city": NYC_REGION, // aliases
  "new-york": NYC_REGION, // (optional)

  // Annapolis + alias
  annapolis: ANNAPOLIS_REGION,
  "annapolis-area": ANNAPOLIS_REGION,

  // Baltimore + aliases
  baltimore: BALTIMORE_REGION,
  "baltimore-area": BALTIMORE_REGION,
  bmore: BALTIMORE_REGION,
};

let __zipIndex; // module-level cache
async function getZipIndex() {
  if (__zipIndex) return __zipIndex;
  const mod = await import("../../../data/zipcodes"); // lazy
  __zipIndex = mod.zipCodes || mod.default;
  return __zipIndex;
}

const removeQueryParam = (paramToRemove) => {
  const next = { ...(router.query || {}) };
  delete next[paramToRemove];
  router.push({ pathname: router.pathname, query: next }, undefined, {
    shallow: true,
  });
};

function useResolvedLocation(props = {}) {
  const router = useRouter();
  const q = router?.query || {};

  const rawCity =
    props.city || props.town || (typeof q.town === "string" ? q.town : "");
  const rawState = props.state || (typeof q.state === "string" ? q.state : "");

  const rawLoc = typeof q.location === "string" ? q.location : "";
  const key = rawLoc.toLowerCase().trim().replace(/\s+/g, "-"); // slugify
  const region = LOCATION_REGIONS[key] || null;

  const city = region ? "" : rawCity;
  const state = region ? region.state : rawState;
  const filterCities = region ? region.cities : [];
  const label = region
    ? region.label
    : rawCity
      ? `${rawCity}${rawState ? `, ${rawState}` : ""}`
      : "";

  return { city, state, region, filterCities, label };
}

// nice label

const SingleLineDistanceFilter = ({
  _value = [{ locationType: "default", miles: 20, zip: "", isValid: false }],
  onUpdate,
  colors,
  compact = false,
}) => {
  const boxStyles = compact
    ? {
        p: 0,
        backgroundColor: "transparent",
        minHeight: "auto",
      }
    : {
        p: 2,
        border: `1px solid ${colors?.primary || "#234990"}`,
        borderRadius: 2,
        backgroundColor: "white",
        minHeight: 56,
      };

  const currentValue = _value[0] || {
    locationType: "default",
    miles: 20,
    zip: "",
    isValid: false,
  };

  const normalizeZip = (z) => {
    if (!z) return "";
    return String(z).split("-")[0].padStart(5, "0");
  };

  const findCoordinatesByZip = async (zipCode) => {
    const zip5 = normalizeZip(zipCode);
    const zipList = await getZipIndex();
    const entry = zipList.find((z) => String(z.zipcode) === zip5);
    return entry ? { latitude: entry.lat, longitude: entry.lon } : null;
  };

  const updateFilter = async (newLocationType, newMiles, newZip = "") => {
    let updated = {
      locationType: newLocationType,
      miles: newMiles,
      zip: newZip,
      latitude: null,
      longitude: null,
      isValid: false,
      message: "",
    };

    if (newLocationType === "default") {
      updated = {
        ...updated,
        latitude: 40.809947662368856,
        longitude: -73.22082853735371,
        message: `${newMiles} miles from default location`,
        isValid: true,
      };
      onUpdate([updated]);
    } else if (newLocationType === "current") {
      try {
        const geo = await detectAndSaveGeoPosition(true);
        if (geo) {
          updated = {
            ...updated,
            latitude: geo.lat,
            longitude: geo.lng,
            message: `${newMiles} miles from current location`,
            isValid: true,
          };
        } else {
          updated = {
            ...updated,
            latitude: 40.809947662368856,
            longitude: -73.22082853735371,
            message: `${newMiles} miles from default location (geolocation failed)`,
            isValid: true,
          };
        }
        onUpdate([updated]);
      } catch (error) {
        console.error("Error getting location:", error);
      }
    } else if (newLocationType === "zip" && newZip) {
      const coords = await findCoordinatesByZip(newZip);
      if (coords) {
        updated = {
          ...updated,
          ...coords,
          message: `${newMiles} miles from ZIP ${newZip}`,
          isValid: true,
        };
      } else {
        updated = {
          ...updated,
          message: `Invalid ZIP code: ${newZip}`,
          isValid: false,
        };
      }
      onUpdate([updated]);
    } else if (newLocationType === "zip") {
      // Just switched to ZIP mode but no ZIP entered yet
      updated = {
        ...updated,
        locationType: "zip",
        miles: newMiles,
        isValid: false,
      };
      onUpdate([updated]);
    }
  };

  const handleLocationTypeChange = (newType) => {
    updateFilter(newType, currentValue.miles, currentValue.zip);
  };

  const handleMilesChange = (newMiles) => {
    updateFilter(currentValue.locationType, newMiles, currentValue.zip);
  };

  const handleZipChange = (e) => {
    const newZip = e.target.value;
    setLocalZip(newZip);
  };

  const handleZipSubmit = () => {
    if (localZip && localZip.length === 5) {
      updateFilter("zip", currentValue.miles, localZip);
    }
  };

  const handleZipKeyDown = (e) => {
    if (e.key === "Enter") {
      handleZipSubmit();
    }
  };

  const [localZip, setLocalZip] = useState(currentValue.zip || "");
  const debouncedZip = useDebounce(localZip, 500);
  useEffect(() => {
    if (currentValue.zip !== localZip) {
      setLocalZip(currentValue.zip || "");
    }
  }, [currentValue.zip]); // Don't include localZip to avoid infinite loop

  useEffect(() => {
    // Only trigger a search if the location type is 'zip' and the debounced value is a valid length
    if (currentValue.locationType === "zip" && debouncedZip.length === 5) {
      updateFilter("zip", currentValue.miles, debouncedZip);
    }
  }, [debouncedZip, currentValue.locationType, currentValue.miles]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        ...boxStyles,
        p: 1,
        border: `1px solid ${colors?.primary || "#234990"}`,
        borderRadius: 1,
        backgroundColor: "white",
        flexWrap: "wrap",
        minHeight: 56,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 60,
          fontWeight: "bold",
          color: colors?.primary || "#234990",
        }}
      >
        Distance:
      </Typography>

      {/* Miles dropdown */}
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <Select
          value={currentValue.miles}
          onChange={(e) => handleMilesChange(e.target.value)}
          displayEmpty
        >
          {[5, 10, 15, 20, 25, 30, 50, 100, 150, 200].map((m) => (
            <MenuItem key={m} value={m}>
              {m} mi
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary">
        from
      </Typography>

      {/* Location type buttons */}
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Button
          size="small"
          variant={
            currentValue.locationType === "default" ? "contained" : "outlined"
          }
          onClick={() => handleLocationTypeChange("default")}
          sx={{
            minWidth: 70,
            textTransform: "none",
            backgroundColor:
              currentValue.locationType === "default"
                ? colors?.primary
                : "transparent",
            borderColor: colors?.primary,
          }}
        >
          Default
        </Button>
        <Button
          size="small"
          variant={
            currentValue.locationType === "current" ? "contained" : "outlined"
          }
          onClick={() => handleLocationTypeChange("current")}
          sx={{
            minWidth: 70,
            textTransform: "none",
            backgroundColor:
              currentValue.locationType === "current"
                ? colors?.primary
                : "transparent",
            borderColor: colors?.primary,
          }}
        >
          My Location
        </Button>
        <Button
          size="small"
          variant={
            currentValue.locationType === "zip" ? "contained" : "outlined"
          }
          onClick={() => handleLocationTypeChange("zip")}
          sx={{
            minWidth: 50,
            textTransform: "none",
            backgroundColor:
              currentValue.locationType === "zip"
                ? colors?.primary
                : "transparent",
            borderColor: colors?.primary,
          }}
        >
          ZIP
        </Button>
      </Box>

      {/* ZIP input (only show when ZIP is selected) */}
      {currentValue.locationType === "zip" && (
        <TextField
          size="small"
          placeholder="12345"
          value={localZip}
          onChange={handleZipChange}
          onKeyDown={handleZipKeyDown}
          sx={{ width: 80 }}
        />
      )}

      {/* Status indicator */}
      {currentValue.isValid && (
        <Typography
          variant="caption"
          color="success.main"
          component="span"
          sx={{
            fontSize: "0.75rem",
            fontWeight: "bold",
          }}
        >
          ✓ Active
        </Typography>
      )}
    </Box>
  );
};

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open, drawerWidth }) => ({
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: open ? drawerWidth : 0, // ✅ Fix left margin when closed
  })
);

const CollapsibleEventTypeFilter = ({
  selectedTypes = [],
  availableTypes = [],
  onChange,
  colors,
  title = "Event Type",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTypeChange = (event) => {
    const value = event.target.value;
    onChange(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveType = (typeToRemove) => {
    onChange(selectedTypes.filter((type) => type !== typeToRemove));
  };

  return (
    <Paper
      sx={{
        p: 2,
        border: `2px solid ${colors?.primary || "#1976d2"}`,
        borderRadius: 2,
        backgroundColor: colors?.secondary || "#f5f5f5",
      }}
    >
      {/* Header with title and expand/collapse button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: colors?.primary || "#1976d2",
            fontWeight: "bold",
          }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={handleToggleExpand}
          size="small"
          sx={{ color: colors?.primary || "#1976d2" }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Always visible chips */}
      <Box sx={{ mb: isExpanded ? 2 : 0 }}>
        {selectedTypes.length > 0 ? (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {selectedTypes.map((type) => (
              <Chip
                key={type}
                label={type}
                onDelete={() => handleRemoveType(type)}
                color="primary"
                variant="filled"
                size="small"
                sx={{
                  backgroundColor: colors?.primary || "#1976d2",
                  color: "white",
                }}
              />
            ))}
            <Chip
              label="Clear All"
              onClick={handleClearAll}
              variant="outlined"
              size="small"
              sx={{
                borderColor: colors?.primary || "#1976d2",
                color: colors?.primary || "#1976d2",
              }}
            />
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            All event types • Click{" "}
            {isExpanded ? (
              "above"
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, verticalAlign: "middle" }} />
            )}{" "}
            to filter
          </Typography>
        )}
      </Box>

      {/* Collapsible filter controls */}
      <Collapse in={isExpanded}>
        <FormControl fullWidth>
          <InputLabel>Select Event Types</InputLabel>
          <Select
            multiple
            value={selectedTypes}
            onChange={handleTypeChange}
            label="Select Event Types"
            renderValue={(selected) =>
              selected.length === 0
                ? "No types selected"
                : `${selected.length} type(s) selected`
            }
          >
            {availableTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                <Checkbox checked={selectedTypes.indexOf(type.id) > -1} />
                <ListItemText primary={type.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography
          variant="caption"
          color="text.secondary"
          component="span"
          sx={{ mt: 1, display: "block" }}
        >
          {selectedTypes.length === 0
            ? `Choose from ${availableTypes.length} event types`
            : `${selectedTypes.length} of ${availableTypes.length} types selected`}
        </Typography>
      </Collapse>
    </Paper>
  );
};

const getDistanceMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const ANNAPOLIS_LOCATION = {
  lat: 38.96772105330125,
  lon: -76.51525587650268,
  miles: 20,
};
const LONGISLAND_LOCATION = {
  lat: 40.79157345621614,
  lon: -73.34159238491179,
  miles: 50,
};
const NYC_LOCATION = {
  lat: 40.75064253774992,
  lon: -73.9876405037836,
  miles: 20,
};
const BALTIMORE_LOCATION = {
  lat: 39.284640974192726,
  lon: -76.61643519845838,
  miles: 25,
};
const OCEAN_CITY_LOCATION = {
  lat: 38.39010373711956,
  lon: -75.06422444816985,
  miles: 35,
};
const attributes = [
  {
    label: "Title",
    name: "title",
    sortable: true,
  },
  {
    label: "Description",
    name: "eventdescription",
    component: {
      type: "html",
    },
  },
  {
    label: "Event Date",
    name: "main_date",
    sortable: true,
    component: {
      type: "dateTime",
    },
  },
  {
    label: "Bands",
    name: "bands",
    state: "bands",
    component: {
      type: "array",
      contextProp: "bandname",
      queryParam: "bandId",
    },
  },
  {
    label: "Venue",
    name: "venue",
    state: "venues",
    component: {
      type: "object",
      contextProp: "venue_name",
      queryParam: "venueId",
    },
  },
];

const LOCATION_PRESETS = {
  annapolis: ANNAPOLIS_LOCATION,
  "long-island": LONGISLAND_LOCATION,
  nyc: NYC_LOCATION,
  baltimore: BALTIMORE_LOCATION,
  "baltimore-area": BALTIMORE_LOCATION,
  "ocean-city": OCEAN_CITY_LOCATION,
};

export default function EventsLayout(props) {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const locationParam = router.query.location;
  const { eventId } = router.query;
  const [state, dispatch] = useAppState();
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const { showSuccessMessage, showInformationMessage } = useNotification();
  const [queryStrings, setQueryString] = useState([]);
  const [paginationProps, setPaginationProps] = useState({ skip: 0, take: 20 });
  const [localPagination, setLocalPagination] = useState({ skip: 0, take: 20 });
  const [currentPage, setCurrentPage] = useState(0);
  const debouncedSearchValue = useDebounce(searchTerm, 500);
  const [enableApi, setEnableApi] = useState(false);
  const [savedFilters, setSavedFilters] = useState(null);
  const isSmallScreen = useIsSmallScreen();
  const [ignoreFilters, setIgnoreFilters] = useState(false);
  const [highlightedEvents, setHighlightedEvents] = useState([]);
  const { setRemoveAppliedFilter, setSaveAppliedFilter, removeAppliedFilter } =
    useFilterContext();

  // Distance filter callback
  const handleDistanceFilterUpdate = useCallback(
    (newFilters) => {
      clearUrlToEvents();
      // Don't allow manual distance changes when location parameter is active
      if (router.query.location) {
        return;
      }

      if (removeAppliedFilter) return;
      setFilters((prev) => ({
        ...prev,
        coordinates: newFilters,
      }));
      setPaginationProps({ skip: 0, take: 20 });
    },
    [removeAppliedFilter, router.query.location]
  );

  // Generic filter change callback
  const handleGenericFilterChange = useCallback((filterName, value) => {
    handleChange(filterName)(value);
  }, []);

  // Event types filter callback
  const handleEventTypesChange = useCallback((value) => {
    clearUrlToEvents();
    setFilters((prev) => ({ ...prev, eventTypes: value }));
    setPaginationProps({ skip: 0, take: 20 });
  }, []);

  // Date filter callback
  const handleDateChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, date: value }));
    setPaginationProps({ skip: 0, take: 20 });
  }, []);

  // Venue filter callback
  const handleVenueChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, venues: value }));
    setPaginationProps({ skip: 0, take: 20 });
  }, []);

  // Band filter callback
  const handleBandChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, bands: value }));
    setPaginationProps({ skip: 0, take: 20 });
  }, []);

  // States filter callback
  const handleStatesChange = useCallback((value) => {
    clearUrlToEvents();
    setFilters((prev) => ({ ...prev, states: value }));
    setPaginationProps({ skip: 0, take: 20 });
  }, []);

  const clearUrlToEvents = useCallback(() => {
    if (!router.isReady) return;
    const hasQuery = router.query && Object.keys(router.query).length > 0;

    // Drop ALL query params and stay on /events without a full reload
    if (router.pathname !== "/events" || hasQuery) {
      router.replace({ pathname: "/events", query: {} }, undefined, {
        shallow: true,
      });
    }

    // Optional: scrub ghost town/state from localStorage so they don't "stick"
    try {
      const saved = JSON.parse(localStorage.getItem("applied_filters") || "{}");
      delete saved.town; // you don’t have a Town UI
      delete saved.state; // remove if URL/state shouldn't persist
      localStorage.setItem("applied_filters", JSON.stringify(saved));
    } catch {}
  }, [router]);

  useEffect(() => {
    // load saved filters FIRST
    const saved = JSON.parse(localStorage.getItem("applied_filters") || "{}");

    // if URL has a location param, apply it ephemerally
    const { location } = router.query || {};
    if (location) {
      setFilters({ ...saved, location });
    } else {
      setFilters(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear the events cache when town/state URL params change
    if (router.query.town || router.query.state) {
      // Force useFetchEvents to do a fresh fetch
      setPaginationProps({ skip: 0, take: 20 });
    }
  }, [router.query.town, router.query.state]);

  useEffect(() => {
    if (savedFilters && typeof savedFilters === "object") {
      setFilters((prev) => ({
        ...prev,
        ...savedFilters,
      }));
    }
  }, [savedFilters]);

  const handleClearAll = () => {
    setFilters({
      bands: [],
      venues: [],
      states: [],
      date: [],
      eventTypes: [],
      coordinates: [
        { locationType: "default", miles: "20", zip: "", isValid: false },
      ],
    });
  };

  const isTabletScreen = useMediaQuery(
    "(min-width: 600px) and (max-width: 900px)"
  );
  const drawerWidth = isTabletScreen ? 300 : 400;
  const initialFilterState = {
    bands: [],
    venues: [],
    states: [],
    date: [],
    eventTypes: [],
    coordinates: [
      { locationType: "default", miles: 20, zip: "", isValid: false },
    ],
  };
  const [filters, setFilters] = useState({
    bands: [],
    venues: [],
    states: [],
    date: [],
    eventTypes: [],
    town: "",
    state: "",
    coordinates: [
      { locationType: "default", miles: 20, zip: "", isValid: false },
    ],
  });

  const handleSaveFilters = useCallback(() => {
    try {
      localStorage.setItem("applied_filters", JSON.stringify(filters));
      showSuccessMessage("Filters saved successfully!");
    } catch (error) {
      console.error("Failed to save filters to local storage", error);
      // You can use a dedicated error notification here if you have one
      // showErrorMessage("Failed to save filters.");
    }
  }, [filters, showSuccessMessage]);

  useEffect(() => {
    const fetchHighlighted = async () => {
      const stored = localStorage.getItem("location_filter");
      const filter = stored ? JSON.parse(stored) : null;

      const now = new Date();

      const q = query(
        collection(db, "events"),
        where("highlight", "==", true),
        where("main_date", ">=", now),
        orderBy("main_date", "asc")
      );

      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {});
      const allEvents = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
      let nearby = allEvents;

      if (filter?.latitude && filter?.longitude && filter?.miles) {
        nearby = allEvents.filter((event) => {
          if (!event.coordinates) return false;
          const dist = getDistanceMiles(
            filter.latitude,
            filter.longitude,
            event.coordinates.latitude,
            event.coordinates.longitude
          );
          return dist <= filter.miles;
        });
      }

      setHighlightedEvents(nearby.slice(0, 3));
      // setHighlightedEvents(allEvents.slice(0, 5));
    };
    fetchHighlighted();
  }, []);


  useEffect(() => {
    const hasCoordinates =
      filters.coordinates?.[0]?.isValid &&
      filters.coordinates?.[0]?.miles > 0 &&
      filters.coordinates?.[0]?.longitude &&
      filters.coordinates?.[0]?.latitude;

    const hasStandardFilters =
      queryStrings.length > 0 &&
      !queryStrings.every((q) => q.field === "coordinates");

    const isJustDefault =
      filters?.coordinates &&
      Array.isArray(filters.coordinates) &&
      filters.coordinates.length > 0 &&
      filters.coordinates[0]?.locationType === "default";

    // ✅ Enable API if any useful filter is present
    if (hasCoordinates || hasStandardFilters) {
      setEnableApi(true);
    } else if (isJustDefault) {
      // ✅ Also enable by default to show all future events
      setEnableApi(true);
    } else {
      setEnableApi(false);
    }
  }, [queryStrings, filters.coordinates, savedFilters]);

  const getFilteredDate = (years) => {
    // ... (your existing getFilteredDate function) ...
  };

  useEffect(() => {
    // Only load saved filters if there's no location parameter
    if (!router.query.location) {
      const storedAppliedFilters = localStorage.getItem("applied_filters");
      if (storedAppliedFilters) {
        try {
          const parsedAppliedFilters = JSON.parse(storedAppliedFilters);
          // if (parsedAppliedFilters.coordinates && parsedAppliedFilters.coordinates.length > 0) {
          setFilters((prev) => ({
            ...prev,
            coordinates: parsedAppliedFilters.coordinates,
          }));
          // }
        } catch (error) {
          console.warn(
            "Failed to parse applied_filters from localStorage",
            error
          );
        }
      }
    } else {
      const storedAppliedFilters = localStorage.getItem("applied_filters");
      if (storedAppliedFilters) {
        try {
          const parsedAppliedFilters = JSON.parse(storedAppliedFilters);
          const { coordinates, ...otherFilters } = parsedAppliedFilters;
          setFilters((prev) => ({
            ...prev,
            ...otherFilters, // Apply saved bands, venues, etc. but not coordinates
          }));
        } catch (error) {
          console.warn(
            "Failed to parse applied_filters from localStorage",
            error
          );
        }
      }
    }
  }, [router.query.location]);

  useEffect(() => {
    // Early return if no date filter
    if (
      !filters?.date ||
      !Array.isArray(filters.date) ||
      filters.date.length === 0
    ) {
      return;
    }

    const dateValue = filters.date[0];
    if (!dateValue) {
      console.warn("Date filter value is empty");
      return;
    }

    const selectedDate = new Date(dateValue);

    // Validate the date
    if (isNaN(selectedDate.getTime())) {
      console.error("Invalid date value:", dateValue);
      return;
    }

    // Your existing date processing logic...
    const startOfDay = new Date(
      Date.UTC(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
        5,
        0,
        0,
        0
      )
    );

    const endOfDay = new Date(
      Date.UTC(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
        28,
        59,
        59,
        999
      )
    );

    // Continue with your logic...
  }, [filters.date]);

useEffect(() => {
  if (!router.isReady) return;

  const { location, town, state: urlState } = router.query;
  const now = new Date();
  
  // Normalize URL parameters
  const qTown = Array.isArray(town) ? town[0] : town;
  const qState = Array.isArray(urlState) ? urlState[0] : urlState;
  
  // Step 1: Check if we have any URL parameters that should take precedence
  const hasUrlParams = location || qTown || qState;
  
  if (hasUrlParams) {
    // URL parameters exist - use them and ignore localStorage
    console.log("Using URL parameters, ignoring localStorage");
    
    if (location) {
      // Handle location presets (long-island, nyc, etc.)
      const key = String(location).toLowerCase().trim().replace(/\s+/g, "-");
      const preset = LOCATION_PRESETS[key];
      
      if (preset) {
        const coord = {
          locationType: "preset",
          miles: String(preset.miles),
          zip: "",
          isValid: true,
          latitude: preset.lat,
          longitude: preset.lon,
          message: `${preset.miles} miles from ${preset.label || key}`,
        };
        
        setFilters({
          ...initialFilterState,
          coordinates: [coord],
          town: "",
          state: "",
        });
      }
    } else if (qTown || qState) {
      // Handle town/state parameters - hard reset filters
      const townClean = (qTown || "").trim();
      const stateClean = (qState || "").trim().toUpperCase();
      
      const onlyTownState = {
        ...initialFilterState,
        town: townClean,
        state: stateClean,
        coordinates: [
          {
            locationType: "default",
            miles: 20,
            zip: "",
            isValid: false, // force geo off
          },
        ],
      };
      
      setFilters(onlyTownState);
      
      // Update localStorage to match URL params and clear conflicting filters
      try {
        const saved = JSON.parse(localStorage.getItem("applied_filters") || "{}");
        saved.town = townClean;
        saved.state = stateClean;
        saved.coordinates = [
          { locationType: "default", miles: 20, zip: "", isValid: false },
        ];
        // Clear other filters that might conflict
        saved.eventtype = [];
        saved.bandIds = [];
        localStorage.setItem("applied_filters", JSON.stringify(saved));
      } catch (e) {
        console.warn("Failed to update localStorage", e);
      }
    }
    
  } else {
    // No URL parameters - use localStorage
    console.log("No URL parameters, using localStorage");
    
    try {
      const saved = JSON.parse(localStorage.getItem("applied_filters") || "{}");
      
      // Clean up ghost town/state from localStorage if no URL params
      const cleaned = { ...saved };
      delete cleaned.town; // you have no Town UI → always remove
      delete cleaned.state; // remove if URL should be the only source
      
      localStorage.setItem("applied_filters", JSON.stringify(cleaned));
      setFilters({
        ...initialFilterState,
        ...cleaned,
      });
    } catch (e) {
      console.warn("Failed to load from localStorage", e);
      setFilters(initialFilterState);
    }
  }
  
  setPaginationProps({ skip: 0, take: 20 });
  setIgnoreFilters(false);
  
}, [router.isReady, router.query.location, router.query.town, router.query.state]);

  useEffect(() => {
    let queryStr = [
      ...(debouncedSearchValue?.length > 0
        ? [
            {
              field: "searchTerms",
              operator: "array-contains-any",
              value: debouncedSearchValue.toLowerCase().split(" "),
            },
          ]
        : []),
      ...(filters.bands && filters.bands.length > 0
        ? [
            {
              field: "bandIds",
              operator: "array-contains-any",
              value: filters.bands.map((v) => v.id),
            },
          ]
        : []),
      ...(filters.eventTypes && filters.eventTypes.length > 0
        ? [
            {
              field: "eventtype",
              operator: "in",
              value: filters.eventTypes,
            },
          ]
        : []),
      ...(filters.venues && filters.venues.length > 0
        ? [
            {
              field: "venue",
              operator: "in",
              value: filters.venues.map((v) => v.id),
            },
          ]
        : []),
      ...(filters.town
        ? [
            {
              field: "city",
              operator: "==",
              value: filters.town,
            },
          ]
        : []),
      ...((filters.states && filters.states.length > 0) || filters.state
        ? [
            {
              field: "state",
              operator: filters.state ? "==" : "in", // Use "==" for single state, "in" for multiple
              value: filters.state
                ? filters.state // Single state from URL
                : filters.states, // Array of states from UI filter
            },
          ]
        : []),
      ...(filters.date && filters.date.length > 0
        ? [
            {
              field: "main_date",
              operator: ">=",
              value: firebase.firestore.Timestamp.fromDate(
                new Date(
                  Date.UTC(
                    new Date(filters.date[0]).getUTCFullYear(),
                    new Date(filters.date[0]).getUTCMonth(),
                    new Date(filters.date[0]).getUTCDate(),
                    5,
                    0,
                    0,
                    0
                  )
                )
              ),
            },
            {
              field: "main_date",
              operator: "<=",
              value: firebase.firestore.Timestamp.fromDate(
                new Date(
                  Date.UTC(
                    new Date(filters.date[0]).getUTCFullYear(),
                    new Date(filters.date[0]).getUTCMonth(),
                    new Date(filters.date[0]).getUTCDate(),
                    28,
                    59,
                    59,
                    999
                  )
                )
              ),
            },
          ]
        : []),

      ...(() => {
        const c0 = filters?.coordinates?.[0] || {};
        const lat = Number(c0.latitude ?? c0.lat);
        const lon = Number(c0.longitude ?? c0.lon);
        const miles = Number(c0.miles ?? 0);

        const valid =
          (c0.isValid ?? true) &&
          Number.isFinite(lat) &&
          Number.isFinite(lon) &&
          miles > 0;

        return valid
          ? [
              {
                field: "coordinates",
                operator: "n/a", // your fetch hook interprets this
                value: [{ ...c0, latitude: lat, longitude: lon, miles }],
              },
            ]
          : [];
      })(),
    ];

    if (!isEqual(queryStr, queryStrings)) {
      setQueryString(queryStr);
      setCurrentPage(0);
    }
  }, [filters, debouncedSearchValue, locationParam]);

  useEffect(() => {
    // ... (your existing useEffect for enabling the API) ...
  }, [queryStrings, filters.coordinates]); // Include filters.coordinates

  const hasLoadedAllEvents = useRef(false);
  const allEventsForFilters = [];
  // const [allEventsForFilters, setAllEventsForFilters] = useState([]);
  // const [allEventsLoaded, setAllEventsLoaded] = useState(false);

  // useEffect(() => {
  //   if (allEventsLoaded) return; // CRITICAL: Prevent re-runs

  //   const fetchAllEventsForFilters = async () => {
  //     try {
  //       const now = new Date();
  //       const q = query(
  //         collection(db, "events"),
  //         where("main_date", ">=", now),
  //         orderBy("main_date", "asc")
  //       );

  //       const snapshot = await getDocs(q);
  //       const allEvents = snapshot.docs.map((doc) => ({
  //         id: doc.id,
  //         ...doc.data(),
  //       }));

  //       setAllEventsForFilters(allEvents);
  //       setAllEventsLoaded(true); // Mark as loaded AFTER setting data
  //     } catch (error) {
  //       console.error("❌ Error fetching all events for filters:", error);
  //     }
  //   };

  //   fetchAllEventsForFilters();
  // }, []);

  const memoizedQueryStrings = useMemo(() => {
    return queryStrings;
  }, [JSON.stringify(queryStrings)]);

  const { events, loading, totalItems, isPaginated } = useFetchEvents(
    paginationProps.take,
    paginationProps.skip,
    memoizedQueryStrings,
    enableApi,
    ignoreFilters,
    debouncedSearchValue
  );

  const availableStates = [
    { id: "NY", name: "NY" },
    { id: "MD", name: "MD" },
    { id: "NJ", name: "NJ" },
    { id: "PA", name: "PA" },
    { id: "VA", name: "VA" },
    { id: "DE", name: "DE" },
    { id: "CT", name: "CT" },
    { id: "MA", name: "MA" },
  ];

  const removeQueryParam = (paramToRemove) => {
    // Create a URLSearchParams object from the current search string
    const params = new URLSearchParams(searchParams.toString());
    // Remove the specified query parameter
    params.delete(paramToRemove);

    // Navigate to the same path but with the modified search parameters
    router.push(`${window.location.pathname}?${params.toString()}`);
  };

  const availableEventTypes = useMemo(() => {
    // Use the cached data from your new town counts system
    if (!allEventsForFilters || allEventsForFilters.length === 0) {
      // Fallback to hardcoded event types if no cached data
      return [
        { id: "Music", name: "Music" },
        { id: "Open Mic", name: "Open Mic" },
        { id: "Karaoke", name: "Karaoke" },
        { id: "Trivia", name: "Trivia" },
        { id: "Comedy", name: "Comedy" },
        { id: "DJ", name: "DJ" },
        { id: "Paint", name: "Paint" },
        { id: "Games", name: "Games" },
        { id: "Fundraiser", name: "Fundraiser" },
        { id: "Play", name: "Play" },
        { id: "Other", name: "Other" },
      ];
    }

    const eventTypesInEvents = allEventsForFilters
      .map((event) => {
        const eventType = event.eventtype || event.event_type || event.type;
        return eventType;
      })
      .filter(Boolean)
      .filter((type) => type.trim() !== "")
      .map((type) => type.trim());

    const uniqueEventTypes = [...new Set(eventTypesInEvents)];

    return uniqueEventTypes.sort().map((typeName) => ({
      id: typeName,
      name: typeName,
    }));
  }, [allEventsForFilters]);

  const persistentFilters = [
    {
      title: "Filter by Distance",
      contextProp: "coordinates",
      type: "distance",
      colorCode: {
        primary: "rgba(35,73,144,1)",
        secondary: "rgba(35,73,144,0.1)",
      },
    },
    {
      title: "Filter by Type",
      contextProp: "eventTypes",
      friendlyName: "eventtype",
      type: "eventTypes",
      colorCode: {
        primary: "rgba(35,73,144,1)",
        secondary: "rgba(255,87,34,0.1)",
      },
    },
    {
      title: "Filter by Date",
      contextProp: "date",
      // friendlyName: "cities",
      type: "date",
      colorCode: {
        primary: "rgba(57,2,118,1)",
        secondary: "rgba(57,2,118,0.1)",
      },
    },
  ];

  const filtersToRender = [
    {
      title: "Filter by Band",
      contextProp: "bands",
      friendlyName: "bandname",
      type: "bands",
      colorCode: {
        primary: "rgba(65,66,183,1)",
        secondary: "rgba(65,66,183,0.1)",
      },
    },
    {
      title: "Filter by Venue",
      contextProp: "venues",
      friendlyName: "venue_name",
      type: "venues",
      colorCode: {
        primary: "rgba(241,86,40,1)",
        secondary: "rgba(241,86,40,0.1)",
      },
    },

    {
      title: "Filter by State",
      contextProp: "states",
      friendlyName: "state",
      type: "checklist",
      colorCode: {
        primary: "rgba(10,172,0,1)",
        secondary: "rgba(10,172,0,0.1)",
      },
    },
    // {
    //   title: "Filter by City",
    //   contextProp: "cities",
    //   friendlyName: "cities",
    //   type: "checklist",
    //   colorCode: {
    //     primary: "rgba(107,0,123,1)",
    //     secondary: "rgba(107,0,123,0.1)",
    //   },
    // },
    // {
    //   title: "Filter by Year",
    //   contextProp: "years",
    //   // friendlyName: "cities",
    //   type: "year",
    //   colorCode: {
    //     primary: "rgba(57,2,118,1)",
    //     secondary: "rgba(57,2,118,0.1)",
    //   },
    // },
  ];

  const renderPersistentFilters = () => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
          justifyContent: "left",
        }}
      >
        {persistentFilters.map((filterName, index) => (
          <Box
            key={filterName?.title + index}
            sx={{
              minWidth: 300,
              maxWidth: 400,
              flex: "1 1 300px",
            }}
          >
            <EventCard
              key={`persistent-${index}`}
              colorCode={filterName?.colorCode?.primary}
              cardTitle={filterName.title}
              collapsible={false} // Always expanded
              defaultExpanded={true}
            >
              <Box
                style={{
                  backgroundColor: "#fbfbfd",
                  padding: "0px",
                }}
              >
                {renderFilter(filterName, true)} {/* Always expanded */}
              </Box>
            </EventCard>
          </Box>
        ))}
      </Box>
    );
  };

  const handleChange = (filterName) => (value) => {
    setPaginationProps({
      skip: 0,
      take: 20,
    });
    setFilters((prev) => ({ ...prev, [filterName.contextProp]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      ...initialFilterState,
      eventTypes: [],
      coordinates: [
        { locationType: "default", miles: "20", zip: "", isValid: false },
      ],
    });
  };

  // Dummy data for select options
  const getFilteredResults = () => {
    let searchResults = events.slice(
      localPagination.skip,
      localPagination.skip + localPagination.take
    );

    // Deduplicate results
    const uniqueResults = [
      ...new Map(searchResults.map((item) => [item.id, item])).values(),
    ];

    return uniqueResults;
  };

  const getFilterContextPropValue = (f) => {
    if (f.type === "checklist" || f.type === "venues" || f.type === "bands") {
      return filters[f.contextProp];
    } else if (f.type === "distance") {
      let val = filters[f.contextProp][0];
      if (
        (val?.locationType === "current" || val?.locationType === "default") &&
        val?.latitude &&
        val?.longitude &&
        val?.miles > 0
      ) {
        return filters[f.contextProp];
      } else if (
        val?.locationType === "zip" &&
        val?.latitude &&
        val?.longitude &&
        val?.miles > -1 &&
        val?.zip
      ) {
        return filters[f.contextProp];
      }

      return [];
    } else if (f.type === "year") {
      return filters[f.contextProp];
    } else return filters[f.contextProp];
  };
  const getAppliedFilterValues = () => {
    return (
      filtersToRender
        // .filter((x) => x.type === "checklist")
        .map((f) => ({
          selectedValues: getFilterContextPropValue(f) || [],
          data:
            f.type === "date"
              ? state?.[f.contextProp]
              : state?.[f.contextProp]
                ? state?.[f.contextProp].map((b, i) => ({
                    id: b?.id
                      ? b?.id
                      : typeof b === "string"
                        ? b
                        : b[f.friendlyName],
                    name: typeof b === "string" ? b : b[f.friendlyName],
                  }))
                : [],
          onChange: handleChange(f),

          ...f,
        }))
        .filter((x) => x.selectedValues.length > 0)
    );
  };
  const renderFilter = (filterName, expandState) => {
    switch (filterName?.type) {
      case "checklist": {
        const isStatesFilter = filterName.contextProp === "states";
        const memoizedData = useMemo(() => {
          if (isStatesFilter) {
            // Use dynamic states from actual events
            return availableStates;
          } else {
            // Use existing logic for other checklist filters
            return (
              state?.[filterName.contextProp]?.map((b) => ({
                id: b?.id
                  ? b?.id
                  : typeof b === "string"
                    ? b
                    : b[filterName.friendlyName],
                name: typeof b === "string" ? b : b[filterName.friendlyName],
              })) ?? []
            );
          }
        }, [state, filterName, isStatesFilter, availableStates]);

        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <ChecklistFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={memoizedOnChange}
            data={memoizedData}
          />
        );
      }

      case "eventTypes": {
        return (
          <CompactChipFilter
            selectedTypes={filters?.eventTypes || []}
            availableTypes={availableEventTypes}
            onChange={handleEventTypesChange}
            colors={filterName?.colorCode}
            title={filterName?.title}
          />
        );
      }
      case "distance": {
        return (
          <SingleLineDistanceFilter
            _value={
              filters?.coordinates || [
                { locationType: "default", miles: 20, zip: "", isValid: false },
              ]
            }
            onUpdate={handleDistanceFilterUpdate}
            colors={filterName?.colorCode}
            compact={true}
          />
        );
      }

      case "eventTypes": {
        const eventTypeData = [
          { id: "Music", name: "Music" },
          { id: "Open Mic", name: "Open Mic" },
          { id: "Karaoke", name: "Karaoke" },
          { id: "Trivia", name: "Trivia" },
          { id: "Comedy", name: "Comedy" },
          { id: "DJ", name: "DJ" },
          { id: "Paint", name: "Paint" },
          { id: "Games", name: "Games" },
          { id: "Fundraiser", name: "Fundraiser" },
          { id: "Play", name: "Play" },
          { id: "Other", name: "Other" },
        ];

        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <ChecklistFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={memoizedOnChange}
            data={eventTypeData}
          />
        );
      }
      case "year": {
        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <YearFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={memoizedOnChange}
          />
        );
      }

      case "date": {
        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <DateFilter
            value={filters?.[filterName?.contextProp]?.[0]}
            onChange={memoizedOnChange}
          />
        );
      }

      case "venues": {
        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <VenueFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={memoizedOnChange}
            isExpanded={expandState}
          />
        );
      }

      case "bands": {
        const memoizedOnChange = useCallback(
          (value) => {
            handleChange(filterName)(value);
          },
          [filterName]
        );

        return (
          <BandFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={memoizedOnChange}
            isExpanded={expandState}
          />
        );
      }

      default:
        return null;
    }
  };

  const [expandedState, setExpandedState] = useState({}); // State to track expanded status of each card

  const handleExpandChange = (filterName, expanded) => {
    setExpandedState((prevState) => ({
      ...prevState,
      [filterName]: expanded, // Update the expanded status for the specific filter
    }));
  };

  const getFiltersToRender = () => {
    if (!filtersToRender || filtersToRender.length === 0) return null;

    const saveFilters = () => {
      setSaveAppliedFilter(true);
      localStorage.setItem("applied_filters", JSON.stringify(filters));
      if (localStorage.getItem("applied_filters")) {
        showSuccessMessage("Filters saved successfully");
      }
    };

    const deleteAddedFilter = () => {
      localStorage.removeItem("applied_filters");
      clearAllFilters();
      showInformationMessage("Saved Filters deleted successfully");
      setSaveAppliedFilter(false);
      setRemoveAppliedFilter(true);
    };

    return (
      <>
        {/* <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1,
        p: 1,
        mt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Button
        variant="outlined"
        color="error"
        onClick={handleClearAll} // This now correctly points to the function above
        startIcon={<DeleteIcon />}
      >
        Clear Filters
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveFilters} // This now correctly points to the function above
        startIcon={<LibraryAddIcon />}
      >
        Save Filters
      </Button>
    </Box> */}

        <AppliedFilters
          filters={getAppliedFilterValues()}
          handleClearAll={clearAllFilters}
          ignoreFilters={ignoreFilters}
        />

        <Box sx={{ display: "flex", width: "100%" }}>
          {/* Parent container */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              mb: "16px",
              p: "0 8px",
              gap: 2,
            }}
          >
            {" "}
            {/* Your Box */}
            {/* <Button
              startIcon={<DeleteIcon />}
              variant="outlined"
              color="error"
              sx={{ flexGrow: 1 }}
              onClick={deleteAddedFilter}
              // disabled={!localStorage.getItem("applied_filters")}
            >
              Delete
            </Button>
            <Button
              startIcon={<LibraryAddIcon />}
              variant="contained"
              sx={{ flexGrow: 1 }}
              disabled={getAppliedFilterValues().length <= 0}
              onClick={saveFilters}
            >
              Save
            </Button> */}
          </Box>
        </Box>

        {filtersToRender.map((filterName, index) => (
          <Box
            key={filterName?.title + index}
            sx={{ margin: "0 8px 16px 8px" }}
          >
            <EventCard
              key={`normal-${index}`}
              colorCode={filterName?.colorCode?.primary}
              cardTitle={filterName.title}
              collapsible
              onExpandChange={(expanded) =>
                handleExpandChange(filterName, expanded)
              }
            >
              <Box
                // padding="0"
                style={{
                  backgroundColor: "#fbfbfd",
                }}
              >
                <Box>{renderFilter(filterName, expandedState[filterName])}</Box>
              </Box>
            </EventCard>
          </Box>
        ))}
      </>
    );
  };

  const CompactChipFilter = ({
    selectedTypes = [],
    availableTypes = [],
    onChange,
    colors,
    title = "Event Type",
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleTypeChange = (event) => {
      const value = event.target.value;
      onChange(typeof value === "string" ? value.split(",") : value);
    };

   return (
<Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #eee" }}>
  {selectedTypes.length > 0 && (
    <Box sx={{ mb: 2, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {selectedTypes.map((type) => (
        <Chip
          key={type}
          label={type}
          onDelete={(e) => {
            e.stopPropagation();
            onChange(selectedTypes.filter((t) => t !== type));
          }}
          size="small"
          color="primary"
        />
      ))}
    </Box>
  )}
  <FormControl fullWidth>
    <InputLabel>Select Event Types</InputLabel>
    <Select
      multiple
      value={selectedTypes}
      onChange={handleTypeChange}
      label="Select Event Types"
      renderValue={(selected) =>
        selected.length === 0
          ? "Select one or more event types"
          : `${selected.length} type(s) selected`
      }
    >
      {availableTypes.map((type) => (
        <MenuItem key={type.id} value={type.id}>
          <Checkbox checked={selectedTypes.indexOf(type.id) > -1} />
          <ListItemText primary={type.name} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>
);
  };
  
  const {
    city,
    state: locState, // <-- alias!
    filterCities = [],
    label,
  } = useResolvedLocation(props);

  return (
    <Box sx={{ display: "flex", paddingLeft: "20px", padding: "20px" }}>
      <Drawer
        variant={isSmallScreen ? "temporary" : "persistent"}
        open={true}
        onClose={() => setOpen(false)}
        sx={{
          width: isSmallScreen ? "100%" : drawerWidth,
          flexShrink: 0,
          zIndex: 1200,
          transition: "transform 0.3s ease-in-out",
          "& .MuiDrawer-paper": {
            //left: "4px",
            width: isSmallScreen ? "100%" : drawerWidth,
            boxSizing: "border-box",
            paddingTop: "90px",
            background: "#eef0f4",
            border: "none",
            borderTopLeftRadius: "8px", // ✅ Adds a rounded top-left corner
            top: "80px", // ✅ Adds space from the top
            right: "100px", // ✅ Ensures it aligns properly
            position: "fixed", // ✅ Keeps it in the right position
            padding: "2px", // ✅ Adds internal spacing
            marginLeft: "10px", // ✅ Adds margin from the left
            paddingLeft: "10px", // ✅ Adds internal spacing
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
          display: open ? "block" : "none",
        }}
      >
        {open && (
          <Button
            onClick={() => setOpen(!open)}
            variant="contained"
            endIcon={<FilterListIcon />}
            sx={{
              height: "48px",
              margin: "0 8px 16px 8px",
              backgroundColor: theme?.primaryColor,
              "&:hover": {
                backgroundColor: theme?.primaryHoverColor,
              },
            }}
          >
            {"Hide Search Filters"}
          </Button>
        )}
        {getFiltersToRender()}
        {
          <Box
            sx={{
              marginTop: "200px",
              display: { xs: "block", sm: "none", md: "none" },
            }}
          ></Box>
        }
      </Drawer>
      <Main
        sx={{
          display: isSmallScreen && open ? "none" : "block",
        }}
        open={open}
      >
        {/* H1 Header for SEO */}
        <h1>Your Ultimate Guide to Live Music Events</h1>
        <h2 className="small-heading">
          Explore live music events, concerts, and local gigs with Webtunes.
          <br />
          Discover your favorite bands, new venues, and upcoming shows near you.
        </h2>

        {/* Breadcrumbs */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <Link href="/" style={{ textDecoration: "none", color: "inherit" }}>
              Home
            </Link>
            {" > "}
            <span style={{ fontWeight: "bold" }}>Events</span>
          </Typography>
        </Box>

        {/* Add breadcrumb schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://webtunes.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Events",
                  item: "https://webtunes.com/events",
                },
              ],
            }),
          }}
        />

        <Box
          sx={{
            textAlign: "left",
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            component="a"
            href="/events/long-island" // Example for Long Island
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Long Island
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events/new-york-city" // Example for NYC
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            NYC
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events/baltimore" // Example for Baltimore
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Baltimore
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events/annapolis"
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Annapolis
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events?location=ocean-city" // Example for Ocean City
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Ocean City
          </Typography>
        </Box>

        {/* <UseMyLocationButton /> */}
        <Box
          sx={{
            minWidth: "200px", // Ensure a minimum width for the search bar
            width: { xs: "500px" }, // Full width on small screens, auto on larger screens
            alignSelf: "center",
          }}
        >
          <CustomSearchBar
            handleChange={(searchTerm) => setSearchTerm(searchTerm)}
            placeholder="Search events..."
          />
        </Box>
        <br></br>
        {/* End Search Events  */}

        {renderPersistentFilters()}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: { xs: "center", sm: "flex-end" }, // Aligns all content to the right on larger screens
            width: "100%",
            gap: { xs: "10px", sm: "30px" },
          }}
        >
          {/* Filter display and button group */}
          {!open && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                flexGrow: { xs: 0, sm: 1 }, // Allows the filter section to grow on larger screens
                gap: { xs: "10px", sm: "30px" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  wordWrap: "break-word",
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                  padding: 1,
                  borderRadius: "4px",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  Current Filter(s):
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <AppliedFilters
                    filters={getAppliedFilterValues()}
                    handleClearAll={handleClearAll}
                    ignoreFilters={false}
                  />
                </Box>
              </Box>
              <Button
                onClick={() => setOpen(true)}
                variant="contained"
                endIcon={<FilterListIcon />}
                sx={{
                  height: "48px",
                  width: { xs: "100%", sm: "250px" },
                  backgroundColor: theme?.primaryColor,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: theme?.primaryHoverColor,
                  },
                }}
              >
                {getAppliedFilterValues().length > 0 && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "white",
                      background: ignoreFilters ? "gray" : "red",
                      borderRadius: "50%",
                      paddingBottom: "3px",
                      height: "16px",
                      width: "16px",
                      marginRight: "5px",
                    }}
                  >
                    {getAppliedFilterValues().length}
                  </div>
                )}
                {"Add / Change Filters"}
              </Button>

              {/* Clear and Save buttons group */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearAll}
                  startIcon={<DeleteIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveFilters}
                  startIcon={<LibraryAddIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Save Filters
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {(city || locState || (filterCities?.length ?? 0) > 0) && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <RecommendedRow
              city={city}
              state={locState}
              filterCities={filterCities}
              label={label}
              max={12}
              minItems={3}
              className="my-8"
            />
          </Box>
        )}

        {highlightedEvents.length > 0 && currentPage === 0 && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              🎯 Highlighted Events Near You —{" "}
              <a
                href="https://www.livemusicfeed.com/listing/"
                style={{ color: "#1976d2", textDecoration: "underline" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                📢 Advertise Here for as little as $10 a week! 🚀
              </a>
            </Typography>
            <Grid container spacing={2}>
              {highlightedEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <EventCard
                    key={`highlight-${event.id}`}
                    cardTitle={event.title}
                    colorCode="#ffd54f"
                    collapsible={false}
                  >
                    {event.imageUrl ? (
                      <img
                        src={event.imageUrl}
                        alt={event.title}
                        style={{
                          width: "100%",
                          maxHeight: 200,
                          objectFit: "contain",
                          marginBottom: 8,
                          borderRadius: 4,
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    ) : event.bands &&
                      event.bands.length > 0 &&
                      event.bands[0].imageUrl ? (
                      <img
                        src={event.bands[0].imageUrl}
                        alt={event.bands[0].bandname || "Band image"}
                        style={{
                          width: "100%",
                          maxHeight: 200,
                          objectFit: "contain",
                          marginBottom: 8,
                          borderRadius: 4,
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    ) : null}

                    {/* 🗓 Date */}
                    <Typography variant="body2" gutterBottom>
                      <strong>Date:</strong>{" "}
                      {event.main_date?.seconds &&
                        new Date(
                          event.main_date.seconds * 1000
                        ).toLocaleString()}
                    </Typography>

                    {/* 📍 Venue */}
                    <Typography variant="body2" gutterBottom>
                      <strong>Venue:</strong> {event.venue_name}, {event.city}
                    </Typography>

                    {/* 🌐 Ticket link */}
                    {event.ticket_url && (
                      <Typography variant="body2" gutterBottom>
                        <strong>Tickets:</strong>{" "}
                        <a
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Buy Now
                        </a>
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <a
                        href={`/events/${event.id}`}
                        style={{
                          textDecoration: "none",
                          color: "#1976d2",
                          fontWeight: "bold",
                        }}
                      >
                        View Event Details →
                      </a>
                    </Typography>
                    {/* 📝 Description */}
                    {/* {event.eventdescription && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {event.eventdescription}
                      </Typography>
                    )} */}
                  </EventCard>

                  {/* <EventCard event={event} /> */}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {events && (
          <EventAccordion
            ignoreFilters={ignoreFilters}
            setIgnoreFilters={() => setIgnoreFilters((i) => !i)}
            loading={loading}
            totalCount={totalItems}
            setPaginationProps={(skip, take) => {
              if (isPaginated) {
                setPaginationProps({
                  skip,
                  take,
                });
              } else {
                setLocalPagination({
                  skip,
                  take,
                });
              }
            }}
            currentPage={currentPage}
            setCurrentPage={(v) => setCurrentPage(v)}
            data={getFilteredResults()}
            attributes={attributes}
          />
        )}
        {events?.length === 0 && !loading && (
          <Typography
            sx={{
              justifyContent: "center",
              display: "flex",
            }}
          >
            No Data found
          </Typography>
        )}
        {eventId && events?.length > 0 && (
          <EventDetails
            event={events.find((e) => e.id == eventId)}
            onDrawerClose={() => {
              removeQueryParam("eventId");
            }}
          />
        )}
      </Main>
    </Box>
  );
}
